import { useState, useEffect, useMemo } from 'react';
import { 
  Filter, 
  Search,
  Calendar,
  AlertTriangle,
  Activity,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { getAllEvents, getAllAlerts, getAllResidents } from '../lib/supabase';
import { useTranslation } from '../contexts/TranslationContext';

export default function Logs() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [residentFilter, setResidentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [dateRange, setDateRange] = useState('24h');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsData, alertsData, residentsData] = await Promise.all([
        getAllEvents(200),
        getAllAlerts(200),
        getAllResidents()
      ]);
      
      setEvents(eventsData || []);
      setAlerts(alertsData || []);
      setResidents(residentsData || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Combine and filter data
  const combinedLogs = useMemo(() => {
    let logs = [];
    
    // Add events
    if (typeFilter === 'all' || typeFilter === 'event') {
      logs = logs.concat(events.map(e => ({
        id: e.id,
        type: 'event',
        timestamp: e.ts,
        resident_id: e.resident_id,
        event_type: e.event_type,
        data: e.data,
        severity: null,
        status: null
      })));
    }
    
    // Add alerts
    if (typeFilter === 'all' || typeFilter === 'alert') {
      logs = logs.concat(alerts.map(a => ({
        id: a.id,
        type: 'alert',
        timestamp: a.created_at,
        resident_id: a.resident_id,
        event_type: a.alert_type,
        data: null,
        severity: a.severity,
        status: a.status,
        acknowledged_at: a.acknowledged_at,
        acknowledged_by: a.acknowledged_by,
        resolved_at: a.resolved_at,
        resolution: a.resolution,
        resolution_note: a.resolution_note
      })));
    }
    
    // Filter by resident
    if (residentFilter !== 'all') {
      logs = logs.filter(l => l.resident_id === residentFilter);
    }
    
    // Filter by status (alerts only)
    if (statusFilter !== 'all') {
      logs = logs.filter(l => l.type === 'event' || l.status === statusFilter);
    }
    
    // Filter by severity (alerts only)
    if (severityFilter !== 'all') {
      logs = logs.filter(l => l.type === 'event' || l.severity === severityFilter);
    }
    
    // Filter by date range
    const now = new Date();
    let cutoff;
    switch (dateRange) {
      case '1h':
        cutoff = new Date(now - 60 * 60 * 1000);
        break;
      case '24h':
        cutoff = new Date(now - 24 * 60 * 60 * 1000);
        break;
      case '7d':
        cutoff = new Date(now - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        cutoff = new Date(now - 30 * 24 * 60 * 60 * 1000);
        break;
      default:
        cutoff = new Date(0);
    }
    logs = logs.filter(l => new Date(l.timestamp) >= cutoff);
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      logs = logs.filter(l => 
        l.event_type?.toLowerCase().includes(query) ||
        l.resident_id?.toLowerCase().includes(query) ||
        JSON.stringify(l.data)?.toLowerCase().includes(query)
      );
    }
    
    // Sort by timestamp descending
    return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [events, alerts, typeFilter, residentFilter, statusFilter, severityFilter, dateRange, searchQuery]);

  // Get resident name helper
  const getResidentName = (residentId) => {
    const resident = residents.find(r => r.id === residentId);
    if (resident) {
      return `${resident.first_name} ${resident.last_name}`;
    }
    return residentId?.substring(0, 8) + '...';
  };

  // Get resident apartment
  const getResidentApt = (residentId) => {
    const resident = residents.find(r => r.id === residentId);
    return resident?.apartments?.apt_label || '--';
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 pt-16 sm:pt-4" data-testid="logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="font-barlow font-bold text-xl sm:text-2xl md:text-3xl tracking-tight">
            {t('event_history')}
          </h1>
          <p className="text-[#a1a1aa] text-sm mt-1">
            {combinedLogs.length} entries
          </p>
        </div>
        
        <Button 
          variant="outline" 
          onClick={fetchData}
          disabled={loading}
          data-testid="refresh-logs"
          size="sm"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
          {t('refresh')}
        </Button>
      </div>
      
      {/* Filters */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <Filter className="h-4 w-4 text-[#52525b]" />
          <span className="text-sm font-medium">Filters</span>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          {/* Search */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52525b]" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-[#09090b] border-[#27272a] text-sm"
                data-testid="search-logs"
              />
            </div>
          </div>
          
          {/* Type Filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="bg-[#09090b] border-[#27272a]" data-testid="filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-[#27272a]">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="event">Events</SelectItem>
              <SelectItem value="alert">Alerts</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Resident Filter */}
          <Select value={residentFilter} onValueChange={setResidentFilter}>
            <SelectTrigger className="bg-[#09090b] border-[#27272a]" data-testid="filter-resident">
              <SelectValue placeholder="Resident" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-[#27272a]">
              <SelectItem value="all">All Residents</SelectItem>
              {residents.map(r => (
                <SelectItem key={r.id} value={r.id}>
                  {r.first_name} {r.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Date Range */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="bg-[#09090b] border-[#27272a]" data-testid="filter-date">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-[#27272a]">
              <SelectItem value="1h">Last Hour</SelectItem>
              <SelectItem value="24h">Last 24 Hours</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-[#09090b] border-[#27272a]" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#18181b] border-[#27272a]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Table */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-xl overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#27272a] hover:bg-transparent">
              <TableHead className="text-[#52525b] text-xs whitespace-nowrap">Timestamp</TableHead>
              <TableHead className="text-[#52525b] text-xs whitespace-nowrap">Resident</TableHead>
              <TableHead className="text-[#52525b] text-xs whitespace-nowrap hidden sm:table-cell">Apt</TableHead>
              <TableHead className="text-[#52525b] text-xs whitespace-nowrap">Type</TableHead>
              <TableHead className="text-[#52525b] text-xs whitespace-nowrap hidden md:table-cell">Details</TableHead>
              <TableHead className="text-[#52525b] text-xs whitespace-nowrap hidden sm:table-cell">Severity</TableHead>
              <TableHead className="text-[#52525b] text-xs whitespace-nowrap">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(10)].map((_, i) => (
                <TableRow key={i} className="border-[#27272a]">
                  {[...Array(7)].map((_, j) => (
                    <TableCell key={j}>
                      <div className="h-4 bg-[#27272a] rounded animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : combinedLogs.length > 0 ? (
              combinedLogs.map((log) => (
                <TableRow 
                  key={`${log.type}-${log.id}`} 
                  className="border-[#27272a] hover:bg-[#27272a]/50"
                  data-testid={`log-row-${log.id}`}
                >
                  <TableCell className="font-mono text-[10px] sm:text-xs whitespace-nowrap">
                    {new Date(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs sm:text-sm">
                    {getResidentName(log.resident_id)}
                  </TableCell>
                  <TableCell className="font-mono text-xs sm:text-sm hidden sm:table-cell">
                    {getResidentApt(log.resident_id)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {log.type === 'alert' ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                      ) : (
                        <Activity className="h-3.5 w-3.5 text-blue-500" />
                      )}
                      <span className="capitalize text-xs sm:text-sm">{log.event_type}</span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate hidden md:table-cell">
                    {log.data ? (
                      <span className="font-mono text-[10px] sm:text-xs text-[#a1a1aa]">
                        {JSON.stringify(log.data)}
                      </span>
                    ) : log.resolution ? (
                      <span className="text-xs sm:text-sm">{log.resolution}: {log.resolution_note}</span>
                    ) : (
                      '--'
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {log.severity && (
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-[9px] sm:text-[10px]",
                          log.severity === 'critical' && "text-red-500 border-red-500/30",
                          log.severity === 'high' && "text-orange-500 border-orange-500/30",
                          log.severity === 'medium' && "text-yellow-500 border-yellow-500/30",
                          log.severity === 'low' && "text-blue-500 border-blue-500/30"
                        )}
                      >
                        {log.severity}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.status && (
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-[9px] sm:text-[10px]",
                          log.status === 'open' && "text-red-500 border-red-500/30",
                          log.status === 'acknowledged' && "text-orange-500 border-orange-500/30",
                          log.status === 'resolved' && "text-green-500 border-green-500/30"
                        )}
                      >
                        {log.status}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#52525b] py-8">
                  No logs found matching your filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
